/**
 * Giesto wishlist — a dependency-free favourites store.
 *
 * Persists to localStorage, syncs across tabs, and drives two custom elements:
 * Signed in, the list also lives on the customer's account (Zara-style): the
 * first sync on a browser merges the guest list into the account, after which
 * the account's list follows the customer to every device.
 *
 *   <giesto-wishlist-toggle>  the heart on a product card
 *   <giesto-wishlist-count>   the badge on the header heart
 *
 * A future wishlist app can take over by listening to `giesto:wishlist:change`
 * or by calling the methods on `window.GiestoWishlist` directly.
 *
 * Saves, removals, favourites-page views and favourites reaching the bag are
 * published as `giesto_wishlist_*` customer events and Clarity events; see
 * docs/wishlist-analytics-2026-10-12.md.
 */
(() => {
  // Several sections each load this asset, so the script can execute more than
  // once per page. Without this guard the second run builds a second store and
  // overwrites window.GiestoWishlist, while the already-registered custom
  // elements stay bound to the first — the header count then stops updating.
  if (window.GiestoWishlist) return;

  const STORAGE_KEY = 'giesto:wishlist';
  const CHANGE_EVENT = 'giesto:wishlist:change';
  const MAX_ITEMS = 250;
  const TOAST_DURATION = 5000;
  let toastTimer = 0;

  /** @typedef {{id: string, handle: string, title: string, url: string, image: string, price: string, addedAt?: number}} WishlistItem */

  /** @param {unknown} value */
  const plainText = (value) => {
    const parsedDocument = new DOMParser().parseFromString(String(value ?? ''), 'text/html');
    return parsedDocument.body.textContent?.trim() ?? '';
  };

  /** @returns {WishlistItem[]} */
  const read = (fallback = []) => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((item) => item && item.id).map((item) => ({ ...item, price: plainText(item.price) }))
        : [];
    } catch {
      return fallback;
    }
  };

  /** @param {WishlistItem[]} items */
  const write = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch {
      /* quota or private mode — keep the in-memory state and carry on */
    }
  };

  const wishlistCopy = () => {
    const english = document.documentElement.lang.toLowerCase().startsWith('en');
    return english
      ? {
          message: 'Item added to your saved items',
          action: 'View saved items',
          label: 'Saved item confirmation',
        }
      : {
          message: 'Ürün kaydedilenlere eklendi',
          action: 'Kaydedilenleri görüntüle',
          label: 'Ürün kaydedildi bildirimi',
        };
  };

  const savedItemsUrl = () => {
    const english = document.documentElement.lang.toLowerCase().startsWith('en');
    return `${english ? '/en' : ''}/pages/favori`;
  };

  const ensureToast = () => {
    let toast = document.querySelector('[data-giesto-wishlist-toast]');
    if (toast) return toast;

    const copy = wishlistCopy();
    toast = document.createElement('aside');
    toast.className = 'giesto-wishlist-toast';
    toast.dataset.giestoWishlistToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-label', copy.label);
    toast.hidden = true;
    toast.innerHTML = `
      <div class="giesto-wishlist-toast__message">
        <svg class="giesto-wishlist-toast__check" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="11" fill="currentColor"></circle>
          <path d="m6.8 12.3 3.2 3.2 7.2-7.2" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="square"></path>
        </svg>
        <span data-giesto-wishlist-toast-message></span>
      </div>
      <a class="giesto-wishlist-toast__action" data-giesto-wishlist-toast-action>
        <span>${copy.action}</span>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 10h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.4"></path></svg>
      </a>`;

    const style = document.createElement('style');
    style.dataset.giestoWishlistToastStyle = '';
    style.textContent = `
      .giesto-wishlist-toast {
        box-sizing: border-box;
        position: fixed;
        z-index: 10000;
        top: max(16px, env(safe-area-inset-top));
        right: max(16px, env(safe-area-inset-right));
        width: min(320px, calc(100vw - 32px));
        padding: 14px;
        border-radius: 6px;
        background: #fff;
        color: #111;
        box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
        font-family: Arial, sans-serif;
        opacity: 0;
        transform: translateY(-8px);
        transition: opacity 180ms ease, transform 180ms ease;
      }
      .giesto-wishlist-toast[hidden] { display: none; }
      .giesto-wishlist-toast[data-visible] { opacity: 1; transform: translateY(0); }
      .giesto-wishlist-toast__message {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        width: 100%;
        font-size: 14px;
        line-height: 20px;
      }
      .giesto-wishlist-toast__check { flex: 0 0 24px; width: 24px; height: 24px; color: #38804a; }
      .giesto-wishlist-toast__message span { flex: 1; }
      .giesto-wishlist-toast__action {
        box-sizing: border-box;
        display: flex;
        width: 100%;
        min-height: 40px;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 8px;
        border: 1px solid #111;
        border-radius: 4px;
        background: #fff;
        color: #111;
        font-size: 14px;
        line-height: 20px;
        text-decoration: none;
      }
      .giesto-wishlist-toast__action:hover,
      .giesto-wishlist-toast__action:focus-visible { background: #111; color: #fff; }
      .giesto-wishlist-toast__action svg { width: 16px; height: 16px; }
      .giesto-wishlist-toast__title { flex: 1; font-weight: 600; }
      .giesto-wishlist-toast__body { margin: 6px 0 0; color: #555; font-size: 13px; line-height: 18px; }
      .giesto-wishlist-toast__close {
        display: inline-flex; align-items: center; justify-content: center;
        flex: 0 0 32px; width: 32px; height: 32px; margin: -6px -6px -6px 0; padding: 0;
        border: 0; background: none; color: #111; cursor: pointer;
      }
      .giesto-wishlist-toast__close svg { width: 18px; height: 18px; }
      .giesto-wishlist-toast__close:focus-visible { outline: 1px solid #111; outline-offset: 2px; }
      @media (prefers-reduced-motion: reduce) {
        .giesto-wishlist-toast { transition: none; }
      }`;

    document.head.append(style);
    document.body.append(toast);
    return toast;
  };

  const showAddedToast = (item, text = '') => {
    const toast = ensureToast();
    const copy = wishlistCopy();
    const message = toast.querySelector('[data-giesto-wishlist-toast-message]');
    const action = toast.querySelector('[data-giesto-wishlist-toast-action]');
    if (message) message.textContent = text || (item?.title ? `${item.title}: ${copy.message}` : copy.message);
    if (action instanceof HTMLAnchorElement) action.href = savedItemsUrl();

    window.clearTimeout(toastTimer);
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.setAttribute('data-visible', ''));
    toastTimer = window.setTimeout(() => {
      toast.removeAttribute('data-visible');
      window.setTimeout(() => {
        if (!toast.hasAttribute('data-visible')) toast.hidden = true;
      }, 180);
    }, TOAST_DURATION);
  };

  let items = read();

  /**
   * @param {WishlistItem | null} changed
   * @param {'add' | 'remove' | 'clear' | 'sync'} action
   */
  const publish = (changed, action) => {
    renderHeader();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        bubbles: true,
        detail: { items: [...items], count: items.length, changed, action },
      })
    );
  };

  // A restored page or suspended tab may have missed a storage event. Refresh
  // before every mutation so its old array cannot resurrect a removed item.
  const refresh = () => {
    const latest = read(items);
    if (JSON.stringify(latest) === JSON.stringify(items)) return;
    items = latest;
    publish(null, 'sync');
  };

  /* ------------------------------------------------------------ analytics -- */

  // The list lives only in this browser, so only the browser can tell whether
  // people save, come back to their favourites and buy them. Events go to
  // Shopify customer events, where a custom pixel can forward them for
  // remarketing, and to Clarity once snippets/giesto-cookie-consent.liquid has
  // loaded it. Both follow the visitor's consent.
  const META_KEY = 'giesto:wishlist:meta';
  const MINUTE = 60 * 1000;
  const DAY = 24 * 60 * MINUTE;
  // A favourites view this long after the latest save is a return visit (GA's
  // session timeout). The raw minutes are sent too, so the cut can change.
  const RETURN_AFTER = 30 * MINUTE;

  /** @returns {{firstSaveAt?: number, lastSaveAt?: number, views?: number, lastViewAt?: number}} */
  const readMeta = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(META_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const writeMeta = (patch) => {
    try {
      localStorage.setItem(META_KEY, JSON.stringify({ ...readMeta(), ...patch }));
    } catch {
      /* same as write() */
    }
  };

  /** @param {number | undefined} since @param {number} unit */
  const elapsed = (since, unit) => (since ? Math.max(0, Math.round((Date.now() - since) / unit)) : null);

  /** @param {WishlistItem} item */
  const productFields = (item) => ({
    product_id: String(item.id),
    product_handle: item.handle || '',
    product_title: item.title || '',
    price: item.price || '',
    days_saved: elapsed(item.addedAt, DAY),
  });

  const clarity = (...args) => {
    try {
      if (typeof window.clarity === 'function') window.clarity(...args);
    } catch {
      /* analytics must never break the list */
    }
  };

  // Session tags make "do savers come back?" a recording filter in Clarity.
  const tagSession = () => {
    const count = items.length;
    clarity('set', 'wishlist_items', count < 2 ? String(count) : count <= 5 ? '2-5' : '6+');
    if (count || readMeta().firstSaveAt) clarity('set', 'wishlist_saver', 'yes');
  };

  /** @param {string} name @param {Record<string, unknown>} [fields] */
  const track = (name, fields = {}) => {
    const page = window.ShopifyAnalytics?.meta?.page;
    try {
      window.Shopify?.analytics?.publish?.(`giesto_wishlist_${name}`, {
        ...fields,
        wishlist_count: items.length,
        page_type: page?.pageType || '',
        signed_in: Boolean(page?.customerId),
      });
    } catch {
      /* analytics must never break the list */
    }
    clarity('event', `wishlist_${name}`);
    tagSession();
  };

  const onWishlistPage = () => Boolean(document.querySelector('giesto-wishlist-page'));

  const trackPageView = () => {
    if (!onWishlistPage()) return;
    const meta = readMeta();
    // Items saved before tracking began carry no time, so any view of them now
    // is necessarily a later visit.
    const returnView = meta.lastSaveAt ? Date.now() - meta.lastSaveAt >= RETURN_AFTER : items.length > 0;
    track('view', {
      return_view: returnView,
      minutes_since_last_save: elapsed(meta.lastSaveAt, MINUTE),
      days_since_first_save: elapsed(meta.firstSaveAt, DAY),
      days_since_last_view: elapsed(meta.lastViewAt, DAY),
      previous_views: Number(meta.views) || 0,
      product_ids: items.slice(0, 50).map((item) => String(item.id)),
    });
    if (returnView) clarity('event', 'wishlist_return_view');
    writeMeta({ views: (Number(meta.views) || 0) + 1, lastViewAt: Date.now() });
  };

  // A favourite reaching the bag, from any surface. Every add path in the
  // theme resolves with the Ajax cart lines, the product id, or both.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const { action, lines, promise } = /** @type {any} */ (event);
    if (action !== 'add' || !(promise instanceof Promise) || !items.length) return;
    const variantIds = (Array.isArray(lines) ? lines : []).map((line) => String(line?.merchandiseId ?? '').split('/').pop());
    promise
      .then((result) => {
        const detail = result?.detail;
        if (!detail || detail.didError) return;
        /** @type {Map<string, string>} product id → variant id */
        const added = new Map();
        for (const line of Array.isArray(detail.items) ? detail.items : []) {
          const variantId = String(line?.variant_id ?? line?.id ?? '');
          if (line?.product_id != null && variantIds.includes(variantId)) added.set(String(line.product_id), variantId);
        }
        if (detail.productId && !added.has(String(detail.productId))) added.set(String(detail.productId), variantIds[0] || '');
        for (const [productId, variantId] of added) {
          const item = items.find((entry) => String(entry.id) === productId);
          if (item) track('add_to_cart', { ...productFields(item), variant_id: variantId, from_wishlist_page: onWishlistPage() });
        }
      })
      .catch(() => {});
  });

  // Clarity loads after consent, usually after this script.
  window.addEventListener('load', tagSession, { once: true });
  document.addEventListener('visitorConsentCollected', tagSession);

  function renderHeader(root = document) {
    for (const link of root.querySelectorAll('[data-testid="header-wishlist"]')) {
      if (link.getAttribute('href') !== savedItemsUrl()) link.setAttribute('href', savedItemsUrl());
      // Profile-menu links keep the icon and label outside the count holder.
      let holder = link.querySelector('[data-giesto-wishlist-count-holder]') || link.querySelector(':scope > span');
      if (!holder) { holder = document.createElement('span'); link.append(holder); }
      const count = items.length;
      let badge = holder.querySelector('[data-giesto-wishlist-badge]');
      if (!count) { if (holder.childNodes.length) holder.replaceChildren(); }
      else {
        if (!badge) {
          badge = document.createElement('span');
          badge.dataset.giestoWishlistBadge = '';
          badge.setAttribute('aria-hidden', 'true');
          badge.style.cssText = 'position:absolute;top:-.4rem;right:1rem;transform:translateX(100%);display:inline-block;box-sizing:border-box;min-width:1.6rem;height:1.6rem;padding:0 .4rem;border-radius:999px;background:rgb(8,81,189);color:white;text-align:center;font:1.2rem/1.8rem var(--font-small-caps,ga-small-caps),Arial,sans-serif;letter-spacing:-.02em';
          const number = document.createElement('span');
          number.style.cssText = 'display:inline-block;transform:translateY(-.25rem)';
          badge.append(number);holder.replaceChildren(badge);
        }
        const value = count > 99 ? '99+' : String(count);
        if (badge.firstChild.textContent !== value) badge.firstChild.textContent = value;
      }
      const label = document.documentElement.lang.toLowerCase().startsWith('en') ? 'Wishlist' : 'Favoriler';
      const accessibleLabel = count ? `${label} (${count})` : label;
      if (link.getAttribute('aria-label') !== accessibleLabel) link.setAttribute('aria-label', accessibleLabel);
    }
  }

  /* -------------------------------------------------------- account sync -- */

  // Signed in, the account holds the list: the email service stores it behind
  // the Shopify app proxy, which signs the customer's id. The first sync on a
  // browser sends the whole local list (the guest's favourites join the
  // account); after that only queued changes go up and the account's list is
  // adopted, so a removal on one device sticks everywhere. Signing out forgets
  // the account's list in this browser, as Zara does.
  const ACCOUNT_KEY = 'giesto:wishlist:account';
  const SYNC_URL = '/apps/giesto-notifications/favorites';

  /** @returns {string | null | undefined} null = signed out, undefined = unknown */
  const customerId = () => {
    if (window.GiestoCustomerId !== undefined) return window.GiestoCustomerId ? String(window.GiestoCustomerId) : null;
    const meta = window.ShopifyAnalytics?.meta;
    if (!meta) return undefined;
    return meta.page?.customerId ? String(meta.page.customerId) : null;
  };

  /** @returns {{customer?: string, seq?: number, ops?: Array<{seq: number, add?: WishlistItem, remove?: string}>} | null} */
  const readAccount = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(ACCOUNT_KEY) || 'null');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  const writeAccount = (value) => {
    try {
      if (value) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(value));
      else localStorage.removeItem(ACCOUNT_KEY);
    } catch {
      /* same as write() */
    }
  };

  const linkedCustomer = () => {
    const id = customerId();
    return id && readAccount()?.customer === id ? id : null;
  };

  /** Collapse queued changes into one request: the latest change per product wins. */
  const collapse = (ops) => {
    const latest = new Map();
    for (const op of ops) latest.set(String(op.add?.id ?? op.remove), op);
    return {
      add: [...latest.values()].filter((op) => op.add).map((op) => op.add),
      remove: [...latest.values()].filter((op) => op.remove).map((op) => String(op.remove)),
    };
  };

  const send = async (body) => {
    const response = await fetch(SYNC_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`favourites sync ${response.status}`);
    const data = await response.json();
    if (!Array.isArray(data?.items)) throw new Error('favourites sync shape');
    return data.items;
  };

  const adopt = (serverItems) => {
    const next = serverItems
      .filter((item) => item && item.id)
      .map((item) => ({ ...item, id: String(item.id), price: plainText(item.price) }))
      .slice(0, MAX_ITEMS);
    if (JSON.stringify(next) === JSON.stringify(items)) return;
    items = next;
    write(items);
    publish(null, 'sync');
  };

  let flushTimer = 0;
  let flushing = false;
  const flush = async () => {
    const customer = linkedCustomer();
    const account = readAccount();
    if (!customer || flushing || !account?.ops?.length) return;
    flushing = true;
    const sent = account.ops;
    try {
      await send(collapse(sent));
      const latest = readAccount();
      const lastSeq = Math.max(...sent.map((op) => op.seq));
      if (latest?.customer === customer) writeAccount({ ...latest, ops: (latest.ops || []).filter((op) => op.seq > lastSeq) });
    } catch {
      /* kept in the queue; the next page load retries */
    } finally {
      flushing = false;
    }
  };

  /** @param {{add?: WishlistItem, remove?: string}} change */
  const queue = (change) => {
    const customer = linkedCustomer();
    if (!customer) return;
    const account = readAccount() || { customer };
    const seq = (Number(account.seq) || 0) + 1;
    writeAccount({ ...account, customer, seq, ops: [...(account.ops || []), { seq, ...change }].slice(-500) });
    window.clearTimeout(flushTimer);
    flushTimer = window.setTimeout(flush, 300);
  };

  const syncAccount = async () => {
    const customer = customerId();
    if (customer === undefined) return;
    const account = readAccount();
    if (customer === null) {
      if (account?.customer) {
        writeAccount(null);
        items = [];
        write(items);
        publish(null, 'sync');
      }
      return;
    }
    const first = account?.customer !== customer;
    const guestItems = first ? [...items] : [];
    const ops = first ? [] : account.ops || [];
    try {
      const serverItems = await send(first ? { add: guestItems, remove: [] } : collapse(ops));
      const latest = readAccount();
      const lastSeq = ops.length ? Math.max(...ops.map((op) => op.seq)) : 0;
      const remaining = !first && latest?.customer === customer ? (latest.ops || []).filter((op) => op.seq > lastSeq) : [];
      writeAccount({ customer, seq: Number(latest?.seq) || 0, ops: remaining });
      adopt(serverItems);
      if (remaining.length) flush();
      if (first && guestItems.length) {
        const english = document.documentElement.lang.toLowerCase().startsWith('en');
        showAddedToast(null, english ? 'Your favourites are saved to your account' : 'Favorilerin hesabına kaydedildi');
        track('account_merge', { merged_items: guestItems.length, account_items: serverItems.length });
      }
    } catch {
      /* the browser list keeps working; the next page load tries again */
    }
  };

  /* ----------------------------------------------------- sign-in prompt -- */

  // A guest whose list reaches PROMPT_AT items is invited, once, to sign in so
  // the list joins their account (see account sync above). It reuses the
  // favourites toast's look, never blocks, and is gone for good once shown.
  const PROMPT_AT = 3;
  const PROMPT_DURATION = 12000;
  let promptTimer = 0;

  const promptCopy = () => (document.documentElement.lang.toLowerCase().startsWith('en')
    ? { title: 'Don’t lose your favourites, sign in', body: 'Sign in and your favourites are saved to your account, on every device.', action: 'Sign in / Register', close: 'Close' }
    : { title: 'Favorilerini kaybetme, giriş yap', body: 'Giriş yaparsan favorilerin hesabına kaydedilir, tüm cihazlarında seninle olur.', action: 'Giriş yap / Üye ol', close: 'Kapat' });

  const loginUrl = () => {
    const base = document.querySelector('#giesto-header-surfaces')?.dataset.loginUrl || '/account/login';
    const url = new URL(base, location.origin);
    const back = location.pathname + location.search;
    url.searchParams.set('return_to', back);
    url.searchParams.set('return_url', back);
    return url.origin === location.origin ? url.pathname + url.search : '/account/login';
  };

  const hidePrompt = (prompt) => {
    window.clearTimeout(promptTimer);
    prompt.removeAttribute('data-visible');
    window.setTimeout(() => { if (!prompt.hasAttribute('data-visible')) prompt.hidden = true; }, 180);
  };

  const showLoginPrompt = () => {
    ensureToast();
    const toast = document.querySelector('[data-giesto-wishlist-toast]');
    if (toast) { window.clearTimeout(toastTimer); toast.removeAttribute('data-visible'); toast.hidden = true; }
    const copy = promptCopy();
    const prompt = document.createElement('aside');
    prompt.className = 'giesto-wishlist-toast giesto-wishlist-toast--prompt';
    prompt.dataset.giestoWishlistPrompt = '';
    prompt.setAttribute('role', 'dialog');
    prompt.setAttribute('aria-modal', 'false');
    prompt.setAttribute('aria-labelledby', 'giesto-wishlist-prompt-title');
    prompt.innerHTML = `
      <div class="giesto-wishlist-toast__message">
        <span id="giesto-wishlist-prompt-title" class="giesto-wishlist-toast__title"></span>
        <button type="button" class="giesto-wishlist-toast__close" data-giesto-wishlist-prompt-close>
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="1.4"></path></svg>
        </button>
      </div>
      <p class="giesto-wishlist-toast__body"></p>
      <a class="giesto-wishlist-toast__action" data-giesto-wishlist-prompt-action><span></span>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 10h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.4"></path></svg>
      </a>`;
    prompt.querySelector('.giesto-wishlist-toast__title').textContent = copy.title;
    prompt.querySelector('.giesto-wishlist-toast__body').textContent = copy.body;
    prompt.querySelector('[data-giesto-wishlist-prompt-action] span').textContent = copy.action;
    prompt.querySelector('[data-giesto-wishlist-prompt-close]').setAttribute('aria-label', copy.close);
    const action = /** @type {HTMLAnchorElement} */ (prompt.querySelector('[data-giesto-wishlist-prompt-action]'));
    action.href = loginUrl();
    action.addEventListener('click', () => track('login_prompt_click'));
    prompt.querySelector('[data-giesto-wishlist-prompt-close]').addEventListener('click', () => { track('login_prompt_dismiss'); hidePrompt(prompt); });
    document.body.append(prompt);
    window.requestAnimationFrame(() => prompt.setAttribute('data-visible', ''));
    promptTimer = window.setTimeout(() => hidePrompt(prompt), PROMPT_DURATION);
    track('login_prompt_shown');
  };

  const maybePromptLogin = () => {
    if (customerId() !== null || items.length < PROMPT_AT || readMeta().loginPromptAt) return false;
    writeMeta({ loginPromptAt: Date.now() });
    showLoginPrompt();
    return true;
  };

  const store = {
    refresh,
    syncHeader: renderHeader,
    get items() {
      return [...items];
    },

    get count() {
      return items.length;
    },

    /** @param {string} id */
    has(id) {
      return items.some((item) => String(item.id) === String(id));
    },

    /** @param {WishlistItem} item */
    add(item) {
      refresh();
      if (!item || !item.id || store.has(item.id)) return false;
      const now = Date.now();
      const normalizedItem = { ...item, id: String(item.id), price: plainText(item.price), addedAt: now };
      items = [normalizedItem, ...items].slice(0, MAX_ITEMS);
      write(items);
      publish(normalizedItem, 'add');
      queue({ add: normalizedItem });
      showAddedToast(normalizedItem);
      writeMeta({ firstSaveAt: readMeta().firstSaveAt || now, lastSaveAt: now });
      track('add', productFields(normalizedItem));
      maybePromptLogin();
      return true;
    },

    /** @param {string} id */
    remove(id) {
      refresh();
      const next = items.filter((item) => String(item.id) !== String(id));
      if (next.length === items.length) return false;
      const removed = items.find((item) => String(item.id) === String(id)) ?? null;
      items = next;
      write(items);
      publish(removed, 'remove');
      if (removed) queue({ remove: String(removed.id) });
      if (removed) track('remove', { ...productFields(removed), from_wishlist_page: onWishlistPage() });
      return true;
    },

    /** @param {WishlistItem} item */
    toggle(item) {
      refresh();
      return store.has(item.id) ? (store.remove(item.id), false) : (store.add(item), true);
    },

    clear() {
      refresh();
      if (!items.length) return;
      for (const item of items) queue({ remove: String(item.id) });
      items = [];
      write(items);
      publish(null, 'clear');
    },
  };

  window.GiestoWishlist = store;

  // Another tab changed the list — adopt it and re-render.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    refresh();
  });

  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });

  // The homepage header hydrates after this script. Keep only its badge bound
  // to the shared store; its existing React menu/layout remains the canonical UI.
  let headerFrame = 0;
  new MutationObserver(() => {
    if (headerFrame) return;
    headerFrame = requestAnimationFrame(() => { headerFrame = 0; renderHeader(); });
  }).observe(document.body, { childList: true, subtree: true });

  /**
   * The heart button on a product card.
   *
   * Renders unpressed on the server so the markup stays cacheable, then
   * corrects itself from localStorage once connected.
   */
  class GiestoWishlistToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.onClick = this.onClick.bind(this);
      this.render = this.render.bind(this);

      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    get item() {
      const { productId, productHandle, productTitle, productUrl, productImage, productPrice } = this.dataset;
      return {
        id: String(productId ?? ''),
        handle: productHandle ?? '',
        title: productTitle ?? '',
        url: productUrl ?? '',
        image: productImage ?? '',
        price: productPrice ?? '',
      };
    }

    /** @param {MouseEvent} event */
    onClick(event) {
      // The card is one big anchor — don't let the heart navigate.
      event.preventDefault();
      event.stopPropagation();
      store.toggle(this.item);
    }

    render() {
      if (!this.button) return;
      const active = store.has(this.dataset.productId ?? '');
      this.button.setAttribute('aria-pressed', String(active));
      this.toggleAttribute('data-active', active);

      const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  /** The count badge next to the header heart. */
  class GiestoWishlistCount extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    render() {
      const { count } = store;
      this.textContent = count > 99 ? '99+' : String(count);
      this.toggleAttribute('hidden', count === 0);
      this.setAttribute('aria-hidden', 'true');

      const control = this.closest('[data-giesto-wishlist-control]');
      if (control && this.dataset.labelTemplate) {
        control.setAttribute('aria-label', this.dataset.labelTemplate.replace('{{ count }}', String(count)));
      }
    }
  }

  if (!customElements.get('giesto-wishlist-toggle')) {
    customElements.define('giesto-wishlist-toggle', GiestoWishlistToggle);
  }

  if (!customElements.get('giesto-wishlist-count')) {
    customElements.define('giesto-wishlist-count', GiestoWishlistCount);
  }
  // Headers/pages can connect before the deferred store asset has executed.
  publish(null, 'sync');

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', trackPageView, { once: true });
  else trackPageView();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAccount, { once: true });
  else syncAccount();
})();
